"use client";

import { useState, useCallback } from "react";
import type { Award, AwardTone } from "@/lib/conditions/awards-types";

function countryFlag(countryCode: string): string {
  if (!countryCode || countryCode.length !== 2) return "";
  const offset = 0x1f1e6;
  const a = countryCode.charCodeAt(0) - 0x41 + offset;
  const b = countryCode.charCodeAt(1) - 0x41 + offset;
  return String.fromCodePoint(a, b);
}

const toneStyles: Record<
  AwardTone,
  { border: string; stat: string; badge: string | null }
> = {
  positive: {
    border: "border-emerald-500/30",
    stat: "text-emerald-600",
    badge: null,
  },
  controversial: {
    border: "border-amber-500/30",
    stat: "text-amber-600",
    badge: "Controversial",
  },
  roast: {
    border: "border-red-500/30",
    stat: "text-red-600",
    badge: "Roast",
  },
};

const badgeBg: Record<string, string> = {
  controversial: "bg-amber-100 text-amber-700",
  roast: "bg-red-100 text-red-700",
};

const categoryLabels: Record<string, string> = {
  headline: "Headline Award",
  controversial: "Controversial Award",
  fun: "Fun Award",
  "data-nerd": "Data Nerd Award",
};

export function AwardCard({ award }: { award: Award }) {
  const [copied, setCopied] = useState(false);
  const tone = toneStyles[award.tone];

  const shareText = `${award.winner.name} just won PeakWave’s “${award.name}” award. ${award.tagline} See the full results: peakwave.co/research/awards/${award.sport}`;

  const handleShare = useCallback(async () => {
    if (typeof navigator !== "undefined" && "share" in navigator) {
      try {
        await navigator.share({
          text: shareText,
          url: `https://peakwave.co/research/awards/${award.sport}#${award.id}`,
        });
        return;
      } catch {
        // User cancelled or share failed, fall through to clipboard
      }
    }

    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = shareText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [shareText, award.sport, award.id]);

  const dataDisclaimer =
    award.dataSource === "weekly"
      ? "Based on weekly aggregated data"
      : award.dataSource === "blended"
        ? "Blended weekly and daily data"
        : null;

  return (
    <div
      id={award.id}
      className={`relative scroll-mt-24 rounded-[20px] border-2 ${tone.border} bg-white shadow-[0_8px_40px_rgba(0,0,0,0.06)] p-8 md:p-12 overflow-hidden`}
    >
      {/* Watermark */}
      <span
        className="absolute bottom-4 right-6 text-[10px] tracking-wider text-text-muted/30 select-none pointer-events-none"
        aria-hidden="true"
      >
        PeakWave Awards
      </span>

      {/* Category label + tone badge */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-text-muted">
          {categoryLabels[award.category] ?? award.category}
        </span>
        {tone.badge && (
          <span
            className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${badgeBg[award.tone]}`}
          >
            {tone.badge}
          </span>
        )}
      </div>

      {/* Award name */}
      <h3 className="font-serif text-[28px] md:text-[40px] leading-[1.1] text-navy mb-4">
        {award.name}
      </h3>

      {/* Winner row */}
      <div className="flex items-center gap-2 mb-6">
        {award.winner.countryCode && (
          <span className="text-xl" aria-hidden="true">
            {countryFlag(award.winner.countryCode)}
          </span>
        )}
        <span className="text-base font-semibold text-charcoal">
          {award.winner.name}
        </span>
        {award.winner.region && (
          <span className="text-sm text-text-muted">
            {award.winner.region}
          </span>
        )}
      </div>

      {/* Big stat */}
      <p
        className={`font-serif text-[56px] md:text-[80px] leading-none ${tone.stat} mb-1`}
      >
        {award.stat}
      </p>

      {/* Stat label */}
      <p className="text-sm text-text-muted mb-6">{award.statLabel}</p>

      {/* Tagline */}
      <p className="font-serif italic text-lg text-charcoal/80 mb-4">
        &ldquo;{award.tagline}&rdquo;
      </p>

      {/* Runner up */}
      {award.runnerUp && (
        <p className="text-sm text-text-muted mb-3">
          Runner up: {award.runnerUp.name} ({award.runnerUp.stat})
        </p>
      )}

      {/* Data source disclaimer */}
      {dataDisclaimer && (
        <p className="text-xs italic text-text-muted/70 mb-4">
          {dataDisclaimer}
        </p>
      )}

      {/* Share button */}
      <button
        onClick={handleShare}
        className="inline-flex items-center gap-2 px-4 min-h-[44px] rounded-lg text-sm font-semibold text-text-muted bg-warm-bg border border-border-light cursor-pointer transition-all hover:border-navy/20 hover:text-navy"
        aria-label={`Share the ${award.name} award`}
      >
        {copied ? (
          <>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Copied
          </>
        ) : (
          <>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
              <polyline points="16 6 12 2 8 6" />
              <line x1="12" y1="2" x2="12" y2="15" />
            </svg>
            Share
          </>
        )}
      </button>
    </div>
  );
}
